import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      // Conversion tracking: stamp the acquisition source (scan/ad src) onto the user once,
      // tying paid traffic -> signup -> first campaign. Fire-and-forget.
      if (res.data && !res.data.acq_src) {
        let src = "", ref = "";
        try { src = localStorage.getItem("ags_src") || ""; ref = localStorage.getItem("ags_ref") || ""; } catch (e) {}
        if (src) {
          api.post("/funnel/attribute", { event: "signup", src, ref })
            .then(() => { try { localStorage.removeItem("ags_src"); localStorage.removeItem("ags_ref"); } catch (e) {} })
            .catch(() => {});
        }
      }
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Capture the acquisition source from any ?src= / ?ref= landing (first-touch) before signup.
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const src = p.get("src");
      if (src && !localStorage.getItem("ags_src")) {
        localStorage.setItem("ags_src", src.slice(0, 40));
        if (p.get("ref")) localStorage.setItem("ags_ref", p.get("ref").slice(0, 40));
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    // Clear in-memory user and go to the landing via client-side nav (no full reload,
    // so we don't immediately re-run /auth/me and risk bouncing back into the app).
    setUser(null);
    setLoading(false);
    navigate("/", { replace: true });
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
